import { useState, useEffect } from 'react';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useRoom } from '../room/RoomProvider';
import {
  getPreferredCameraDeviceId,
  getPreferredMicrophoneDeviceId,
  setPreferredCameraDeviceId,
  setPreferredMicrophoneDeviceId,
} from '../video/cameraDevicePreference';
import { getMediaDeviceLabel, getPublishedCameraDevice, waitForPublishedCameraDevice } from '../video/cameraDeviceSelection';
import {
  dispatchCameraDeviceSwitchFinished,
  dispatchCameraDeviceSwitchStarted,
  dispatchCameraDeviceTrackRefreshed,
} from '../video/cameraDeviceEvents';
import { logVideoDiagnostic } from '../video/videoDiagnostics';
import { Modal } from '../../components/Modal';

interface DeviceSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeviceSelector({ isOpen, onClose }: DeviceSelectorProps) {
  const room = useRoomContext();
  const { roomCode } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [actualCameraDeviceId, setActualCameraDeviceId] = useState<string | null>(null);
  const [pendingCameraDeviceId, setPendingCameraDeviceId] = useState<string | null>(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');

  // Load available devices
  useEffect(() => {
    async function loadDevices() {
      try {
        // Request permissions first, then immediately release the temporary
        // tracks so the real LiveKit camera publication keeps ownership.
        const permissionStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        permissionStream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        setCameras(devices.filter(d => d.kind === 'videoinput'));
        setMicrophones(devices.filter(d => d.kind === 'audioinput'));

        const audioTrack = localParticipant?.getTrackPublication(Track.Source.Microphone)?.track;

        setActualCameraDeviceId(getPublishedCameraDevice(localParticipant).deviceId);
        if (audioTrack?.mediaStreamTrack) {
          const settings = audioTrack.mediaStreamTrack.getSettings();
          setSelectedMicrophone(settings.deviceId || '');
        } else {
          setSelectedMicrophone(getPreferredMicrophoneDeviceId() || '');
        }
      } catch (error) {
        console.error('Error loading devices:', error);
      }
    }

    if (isOpen) {
      loadDevices();
    }
  }, [isOpen, localParticipant]);

  const handleCameraChange = async (deviceId: string) => {
    setPendingCameraDeviceId(deviceId);
    dispatchCameraDeviceSwitchStarted();
    const context = {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    };

    try {
      await room.switchActiveDevice('videoinput', deviceId, true);
      setPreferredCameraDeviceId(deviceId);
      const actualDevice = await waitForPublishedCameraDevice(localParticipant, deviceId);
      setActualCameraDeviceId(actualDevice.deviceId);
      logVideoDiagnostic(actualDevice.deviceId === deviceId ? 'info' : 'warn', 'camera-device-switched', {
        requestedDeviceId: deviceId,
        requestedDeviceLabel: getMediaDeviceLabel(cameras, deviceId),
        actualDeviceId: actualDevice.deviceId,
        actualDeviceLabel: actualDevice.label,
        actualWidth: actualDevice.width,
        actualHeight: actualDevice.height,
        actualFrameRate: actualDevice.frameRate,
      }, context);
      dispatchCameraDeviceTrackRefreshed();
    } catch (error) {
      const actualDevice = getPublishedCameraDevice(localParticipant);
      setActualCameraDeviceId(actualDevice.deviceId);
      console.error('Error switching camera:', error);
      logVideoDiagnostic('error', 'camera-device-switch-failed', {
        error,
        requestedDeviceId: deviceId,
        requestedDeviceLabel: getMediaDeviceLabel(cameras, deviceId),
        actualDeviceId: actualDevice.deviceId,
        actualDeviceLabel: actualDevice.label,
      }, context);
    } finally {
      setPendingCameraDeviceId(null);
      dispatchCameraDeviceSwitchFinished();
    }
  };

  const handleMicrophoneChange = async (deviceId: string) => {
    setSelectedMicrophone(deviceId);
    try {
      await room.switchActiveDevice('audioinput', deviceId);
      setPreferredMicrophoneDeviceId(deviceId);
    } catch (error) {
      console.error('Error switching microphone:', error);
    }
  };

  if (!isOpen) return null;

  const selectedCameraValue = pendingCameraDeviceId || actualCameraDeviceId || getPreferredCameraDeviceId() || '';

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/40"
      panelClassName="bg-panel border border-line rounded-2xl p-6 max-w-md w-full space-y-6 shadow-xl"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Device settings"
    >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-medium text-text">Device Settings</h2>
          <button
            onClick={onClose}
            className="text-text-dim hover:text-text transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Camera Selection */}
        <div className="space-y-2">
          <label className="label-mono">Camera</label>
          <select
            value={selectedCameraValue}
            onChange={(e) => handleCameraChange(e.target.value)}
            className="input w-full"
            style={{ backgroundColor: '#f9f9f6', color: '#151515' }}
          >
            {cameras.length === 0 ? (
              <option value="" style={{ backgroundColor: '#f9f9f6', color: '#151515' }}>No cameras found</option>
            ) : (
              cameras.map((camera) => (
                <option key={camera.deviceId} value={camera.deviceId} style={{ backgroundColor: '#f9f9f6', color: '#151515' }}>
                  {camera.label || `Camera ${cameras.indexOf(camera) + 1}`}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Microphone Selection */}
        <div className="space-y-2">
          <label className="label-mono">Microphone</label>
          <select
            value={selectedMicrophone}
            onChange={(e) => handleMicrophoneChange(e.target.value)}
            className="input w-full"
            style={{ backgroundColor: '#f9f9f6', color: '#151515' }}
          >
            {microphones.length === 0 ? (
              <option value="" style={{ backgroundColor: '#f9f9f6', color: '#151515' }}>No microphones found</option>
            ) : (
              microphones.map((mic) => (
                <option key={mic.deviceId} value={mic.deviceId} style={{ backgroundColor: '#f9f9f6', color: '#151515' }}>
                  {mic.label || `Microphone ${microphones.indexOf(mic) + 1}`}
                </option>
              ))
            )}
          </select>
        </div>

        <button onClick={onClose} className="btn btn-accent w-full">
          Done
        </button>
    </Modal>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
